import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ThemeToggle from '../components/ThemeToggle';

const { FiUser, FiLock, FiArrowLeft } = FiIcons;

function Login() {
  const navigate = useNavigate();
  const { login } = useApp();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'customer'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock authentication
    const userData = {
      id: '1',
      email: formData.email,
      type: formData.userType,
      name: formData.userType === 'customer' ? 'John Doe' : 
            formData.userType === 'prison' ? 'Prison Manager' : 'System Admin',
      prisonId: formData.userType === 'prison' ? 'hmp-belmarsh' : null
    };
    
    login(userData);
    
    // Redirect based on user type
    switch (formData.userType) {
      case 'customer':
        navigate('/customer');
        break;
      case 'prison':
        navigate('/prison');
        break;
      case 'admin':
        navigate('/admin');
        break;
      default:
        navigate('/customer');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-government-50 to-primary-50 dark:from-government-900 dark:to-government-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8 relative"
      >
        <div className="absolute top-0 right-0">
          <ThemeToggle />
        </div>
        
        <div className="bg-white dark:bg-government-900 rounded-2xl shadow-xl p-8 transition-colors duration-300">
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => navigate('/')}
              className="absolute top-4 left-4 p-2 text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
            </button>
            
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="bg-primary-500 p-3 rounded-xl">
                <img 
                  src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                  alt="AB Orders Logo" 
                  className="w-8 h-8"
                />
              </div>
              <h1 className="text-2xl font-bold text-government-900 dark:text-white">AB Orders</h1>
            </div>
            <p className="text-government-600 dark:text-government-300">Sign in to your account</p>
          </div>

          {/* User Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-3">
              Account Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'customer', label: 'Visitor' },
                { value: 'prison', label: 'Prison' },
                { value: 'admin', label: 'Admin' }
              ].map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, userType: type.value }))}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    formData.userType === type.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'border-government-200 dark:border-government-600 text-government-600 dark:text-government-300 hover:border-government-300 dark:hover:border-government-500'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-800 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Password
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-800 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors duration-200"
            >
              Sign In
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 p-4 bg-government-50 dark:bg-government-800 rounded-lg transition-colors duration-300">
            <p className="text-xs text-government-600 dark:text-government-400 text-center">
              Demo Mode: Use any email/password combination
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;